import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import useOnclickOutside from "react-cool-onclickoutside";
import usePlacesAutocomplete, {
  LatLng,
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import { useCustomerAnalytics } from "../../../../services/hooks/useCustomerAnalytics";
import { useDetectCurrentLocation } from "../../../../services/hooks/useDetectCurrentLocation";
import { useModalHook } from "../../../../services/hooks/useModal";
import { usePeliasAutocomplete } from "../../../../services/hooks/usePeliasAutocomplete";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import { setSelectedFranchise } from "../../../../services/redux/slices/configSlice";
import {
  setBackPage,
  setCurrentDeliveryConfig,
  setSavedAddresses,
  setSelectedAddress,
  setTempLocation,
} from "../../../../services/redux/slices/deliverySlice";
import {
  LoadingActionEnum,
  decreasePendingOperations,
  increasePendingOperations,
  pushToLoadingStack,
  setAddressSelectionDrawerState,
  setAnimatedGlobalBannerState,
  setLoading,
} from "../../../../services/redux/slices/globalComponentsToggleSlice";
import { addCustomerAddress } from "../../../../utils/APICalls/APICalls";
import {
  PeliasResult,
  peliasReverseGeocode,
} from "../../../../utils/APICalls/peliasAPI";
import {
  COOKIE_ENUM,
  CUSTOMER_ANALYTICS_EVENT,
  LocalStorageEnum,
  OrderTypesEnum,
  PagesEnum,
  URL_PARAMS,
} from "../../../../utils/enums";
import { getPageLink } from "../../../../utils/help/navigation/navigation";
import { isVisitor } from "../../../../utils/help/user/user";
import {
  Address,
  DeliveryConfigZone,
  Franchise,
  FranchiseDeliveryConfig,
  RestaurantTypeEnum,
  activateAddressColumns,
} from "../../../../utils/types";
import { selectStoredDeliveryConfig } from "../../../../services/redux/selectors/deliverySelectors";

interface SearchBoxProps {
  setCenter: (center: { lat: number; lng: number }) => void;
  center: { lat: number; lng: number };
  setZoom: (zoom: number) => void;
  detailsSection: boolean;
  setDetailsSection: (bol: boolean) => void;
  setError: (value: { text: string; success?: boolean } | null) => void;
  zoom: number;
  onSaveAddressSuccess?: () => void;
  activeDeliveryConfig?: FranchiseDeliveryConfig | null;
}

interface DeliveryZoneInfo {
  deliveryZoneId: number;
  minEstimatedTime: number;
  maxEstimatedTime: number;
  deliveryFees: number;
}

export const useSearchBox = (props: SearchBoxProps) => {
  const {
    detailsSection,
    setDetailsSection,
    setError,
    center,
    setCenter,
    setZoom,
    zoom,
    onSaveAddressSuccess,
    activeDeliveryConfig,
  } = props;

  // Hooks
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { t } = useTranslationI18();
  const { handleModal } = useModalHook();
  const addCustomerAnalytics = useCustomerAnalytics();

  // State
  const [displayLogin, setDisplayLogin] = useState(false);
  const [disableSaveAddressButton, setDisableSaveAddressButton] =
    useState(false);
  const [disableStreetNameInput, setDisableStreetNameInput] = useState(false);

  // Router query params
  const { franchise, next_page } = router.query;

  // Selectors
  const delivery = useAppSelector((state) => state.delivery);
  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );
  const franchiseConfig = useAppSelector(
    (state) => state.config.selectedFranchise?.franchiseConfig
  );
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const restaurantConfig = useAppSelector(
    (state) => state.config.restaurantInfo
  )?.config;
  const restaurantType = useAppSelector(
    (state) => state.config.restaurantInfo?.type
  );
  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );
  const franchises =
    useAppSelector((state) => state.config.activeFranchises) || [];
  // The stored config un-re-pointed: this hook holds another branch's config
  // on purpose while it works out which branch covers the address, and needs
  // that config's own zones to do it. See `selectStoredDeliveryConfig`.
  const currentDeliveryConfig = useAppSelector(selectStoredDeliveryConfig);
  const preferredCountry = useAppSelector(
    (state) => state.config.restaurantInfo?.preferredCountry
  );

  // Derived state
  const isEcommerce = restaurantType === RestaurantTypeEnum.ECOMMERCE;
  const [selectedAddressState, setSelectedAddressState] =
    useState<Address | null>(delivery.selectedAddress);

  const enableInternalMaps = restaurantConfig?.enableInternalMaps ?? false;
  const enableNewDeliveryExperience =
    restaurantConfig?.enableNewDeliveryExperience ?? false;

  // Initialize disableStreetNameInput from restaurantConfig
  useEffect(() => {
    setDisableStreetNameInput(restaurantConfig?.addressFormAutoFill ?? false);
  }, [restaurantConfig]);

  const isMainDark = restaurantInfo?.colors?.theme === "main_dark";

  // Translations
  const translations = {
    successfullySwitched: t("delivery:successfly-switched-franchise"),
    enableLocation: t("delivery:enable-location"),
    connectionTimeout: t("delivery:connection-timeout"),
    outsideDelivery: t("delivery:outside-delivery"),
    pleaseSelectSavedAddress: t("delivery:please-select-saved-address"),
    area: t("delivery:area"),
    buildingNumber: t("delivery:building-number"),
    floorNumber: t("delivery:floor-number"),
    apartmentNumber: t("delivery:apartment-number"),
    addressTitle: t("delivery:address-title"),
    additionalDetails: t("delivery:additional-details"),
    saveAddress: t("delivery:save-address"),
    searchBarText: t("delivery:search-bar-text"),
    detectLocation: t("delivery:detect-location"),
    login: t("delivery:login"),
    savedAddresses: t("delivery:saved-addresses"),
    toViewAddress: t("delivery:to-view-address"),
    confirmAddress: t(
      `${
        isEcommerce && !next_page
          ? "delivery:confirm-location-btn"
          : "delivery:confirm-address-btn"
      }`
    ),
    deliveryType: t("delivery:delivery-type"),
    fieldRequired: t("delivery:field-required"),
    onlySpaces: t("delivery:only-spaces"),
    validationMsg: t("delivery:validation-msg"),
    addressError: t("delivery:address-error"),
    streetName: t("delivery:street-name"),
    addressTitlePlaceholder: t("delivery:address-title-label"),
  };

  const {
    ready: finalReady,
    value: finalValue,
    suggestions: { status: finalStatus, data: finalData },
    setValue: finalSetValue,
    clearSuggestions: finalClearSuggestions,
  } = enableInternalMaps
    ? // enableInternalMaps is fixed per restaurant config, so the branch is
      // stable across renders; calling both hooks would boot Google Places
      // even where it is disabled.
      // eslint-disable-next-line react-hooks/rules-of-hooks
      usePeliasAutocomplete({
        countrycodes: preferredCountry,
        limit: 10,
        debounce: 300,
        focus: {
          lat: center.lat,
          lng: center.lng,
        },
      })
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      usePlacesAutocomplete({
        requestOptions: {
          componentRestrictions: {
            country: preferredCountry || null,
          },
        },
        debounce: 300,
      });

  const ref = useOnclickOutside(() => finalClearSuggestions());

  // The pin position when the box mounted. Moving the map clears
  // selectedAddress (useMapScreen onChange) while leaving the search input
  // empty, so a pin that differs from this one counts as a deliberate
  // location, same as typing an address.
  const initialCenterRef = useRef(center);
  const isSamePoint = (
    a: { lat: unknown; lng: unknown },
    b: { lat: unknown; lng: unknown }
  ) =>
    `${a.lat}`.slice(0, 7) === `${b.lat}`.slice(0, 7) &&
    `${a.lng}`.slice(0, 7) === `${b.lng}`.slice(0, 7);

  // Effects
  useEffect(() => {
    // Check if user is visitor
    setDisplayLogin(isVisitor());

    if (isEcommerce && !next_page) {
      setDisplayLogin(false);
    }

    // Handle switch branch address from localStorage
    const switchBranchAddress = localStorage.getItem(
      LocalStorageEnum.SWITCH_ADDRESS
    );
    if (switchBranchAddress) {
      for (let index = 0; index < delivery.savedAddresses.length; index++) {
        const address = delivery.savedAddresses[index];
        if (switchBranchAddress === address.id?.toString()) {
          dispatch(setSelectedAddress(address));
          setError({
            text: translations.successfullySwitched,
            success: true,
          });
          localStorage.removeItem(LocalStorageEnum.SWITCH_ADDRESS);
          break;
        }
      }
    }
  }, [delivery.savedAddresses, next_page]);

  // Handlers
  const handleInput = (e: { target: { value: string } }) => {
    // Map address search — report once per search session (empty -> non-empty),
    // not on every keystroke. V3 only.
    if (
      restaurantConfig?.hasWelcomeDrawerV3 &&
      !finalValue?.trim() &&
      e.target.value.trim()
    ) {
      addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.USED_SEARCH_ON_MAP);
    }
    finalSetValue(e.target.value);
  };

  const handleSelect = ({
    description,
    place_id,
  }: {
    description: any;
    place_id: string;
  }) => {
    dispatch(setSelectedAddress(null));
    finalSetValue(description, false);
    finalClearSuggestions();
    setZoom(17);

    if (enableInternalMaps) {
      const selectedResult = finalData.find(
        (result: any) => result.place_id.toString() === place_id
      );
      if (
        selectedResult &&
        "lat" in selectedResult &&
        "lon" in selectedResult
      ) {
        const newCenter = {
          lat: parseFloat(selectedResult.lat),
          lng: parseFloat(selectedResult.lon),
        };
        setCenter(newCenter);
      }

      addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.MAP_SEARCH_DROPDOWN_CLICK, {
        searchQuery: finalValue,
        searchResult: description,
      });
    } else {
      getGeocode({ placeId: place_id }).then((results) => {
        const { lat, lng } = getLatLng(results[0]);
        setCenter({ lat, lng });
      });
    }
  };

  const handleSelectSavedLocations = (address: Address) => {
    dispatch(setSelectedAddress(address));
    dispatch(setTempLocation({ lat: address.lat, lng: address.lng }));
    setSelectedAddressState(address);
    setCenter({ lat: address.lat, lng: address.lng });
    setZoom(17);
  };

  const { getCurrentPosition } = useDetectCurrentLocation({
    onCenterChange: (center) => {
      setCenter(center);
      setZoom(17);
    },
    onErrorStateChange: setError,
    onAddressSelect: handleSelect,
    onValueSet: finalSetValue,
    functionName: "searchDrawer",
  });

  const handleLoadingState = (isLoading: boolean) => {
    if (restaurantConfig?.loadingStateSolution) {
      const action = isLoading
        ? LoadingActionEnum.INCREASE
        : LoadingActionEnum.DECREASE;
      const functionName = `searchDrawer ${
        isLoading ? "" : "success "
      }getCurrentPosition`;

      dispatch(
        pushToLoadingStack({
          action,
          functionName,
          date: new Date(),
        })
      );

      if (isLoading) {
        dispatch(increasePendingOperations());
      } else {
        dispatch(decreasePendingOperations());
      }
    } else {
      dispatch(setLoading(isLoading));
    }
  };

  // Point-in-polygon algorithm for Pelias (Ray casting algorithm)
  const pointInPolygon = (
    point: { lat: number; lng: number },
    polygon: { lat: number; lng: number }[]
  ): boolean => {
    let inside = false;

    for (let i = 0; i < polygon.length; i++) {
      const j = (i + 1) % polygon.length;
      const vertex1 = polygon[i];
      const vertex2 = polygon[j];

      if (rayIntersectsEdge(point, vertex1, vertex2)) {
        inside = !inside;
      }
    }

    return inside;
  };

  const rayIntersectsEdge = (
    point: { lat: number; lng: number },
    vertex1: { lat: number; lng: number },
    vertex2: { lat: number; lng: number }
  ): boolean => {
    const crossesHorizontalLine =
      vertex1.lat > point.lat !== vertex2.lat > point.lat;

    if (!crossesHorizontalLine) {
      return false;
    }
    const intersectionLng =
      vertex1.lng +
      ((vertex2.lng - vertex1.lng) * (point.lat - vertex1.lat)) /
        (vertex2.lat - vertex1.lat);

    return intersectionLng > point.lng;
  };

  // Confirm can be tapped before the Maps script has settled — in-app browsers
  // (Instagram, Facebook) are slow enough for that to be routine — and then
  // `new google.maps.Polygon` is not a constructor. Check rather than assume.
  const isGoogleGeometryReady = (): boolean =>
    typeof google !== "undefined" &&
    Boolean(google.maps?.Polygon) &&
    Boolean(google.maps?.geometry?.poly?.containsLocation);

  // Delivery zone check
  const checkInDeliveryZone = (
    center: LatLng,
    deliveryZones: DeliveryConfigZone[]
  ): DeliveryZoneInfo | null => {
    // Under hasWelcomeDrawerV3 an address is resolved by walking every
    // franchise's zones (autoSelectDeliveryFranchise), so zone data belonging
    // to branches the customer never picked is now on the hot path. One branch
    // with missing or degenerate zone data used to throw here and take the
    // whole Confirm with it, leaving the button dead — skip what can't be
    // tested and keep searching the remaining zones.
    for (let index = 0; index < deliveryZones.length; index++) {
      const deliveryZone = deliveryZones[index]?.deliveryZone?.polygon;

      // Fewer than three vertices is not a polygon: Google builds one whose
      // getPath() is undefined and containsLocation then throws on getLength.
      if (!Array.isArray(deliveryZone) || deliveryZone.length < 3) {
        continue;
      }

      const deliveryZoneInLatLng = deliveryZone.map((latlng: number[]) => {
        return { lat: latlng[0], lng: latlng[1] };
      });

      let isInZone = false;

      if (enableInternalMaps || !isGoogleGeometryReady()) {
        isInZone = pointInPolygon(center, deliveryZoneInLatLng);
      } else {
        const deliveryAreaPolygon = new google.maps.Polygon({
          paths: deliveryZoneInLatLng,
        });

        isInZone = google.maps.geometry.poly.containsLocation(
          center,
          deliveryAreaPolygon
        );
      }

      if (isInZone) {
        return {
          minEstimatedTime: deliveryZones[index].minEstimatedTime,
          maxEstimatedTime: deliveryZones[index].maxEstimatedTime,
          deliveryFees: deliveryZones[index].deliveryFees,
          deliveryZoneId: deliveryZones[index].deliveryZoneId,
        };
      }
    }

    return null;
  };

  // Find available franchise for delivery
  const findAvailableFranchise = (selectedAddress?: Address) => {
    for (let index = 0; index < franchises.length; index++) {
      const franchiseCur = franchises[index];

      if (
        franchiseCur.slug !== franchise &&
        franchiseCur.franchiseConfig.deliveryV2
      ) {
        const franchiseDeliveryConfigCurr: FranchiseDeliveryConfig[] =
          franchiseCur.franchiseConfig.deliveryV2.filter(
            () => !franchiseCur.franchiseConfig.catering
          );

        const franchiseCurDeliveryConfig = franchiseDeliveryConfigCurr.find(
          (config) =>
            config.deliveryType.id === currentDeliveryConfig?.deliveryType.id
        );

        if (franchiseCurDeliveryConfig) {
          const inZone = checkInDeliveryZone(
            {
              lat: selectedAddress?.lat || center.lat,
              lng: selectedAddress?.lng || center.lng,
            },
            franchiseCurDeliveryConfig.deliveryConfigZones
          );

          if (inZone) {
            return franchiseCur;
          }
        }
      }
    }

    return null;
  };

  // Handle outside delivery zone
  const handleOutsideDeliveryZone = (selectedAddress?: Address) => {
    const availableFranchise = findAvailableFranchise(selectedAddress);

    if (availableFranchise) {
      handleModal(true, "SwitchFranchise/SwitchFranchise", {
        componentProps: {
          franchise: availableFranchise,
          returnPage: delivery.selectedAddress
            ? PagesEnum.Home
            : PagesEnum.Location,
          deliveryAddressId: selectedAddress
            ? selectedAddress.id
            : delivery.selectedAddress?.id,
        },
      });
    } else {
      setError({
        text: translations.outsideDelivery,
      });

      if (enableInternalMaps) {
        peliasReverseGeocode({
          point: {
            lat: center.lat,
            lon: center.lng,
          },
        })
          .then((result: any) => {
            addCustomerAnalytics(
              CUSTOMER_ANALYTICS_EVENT.OUT_OF_DELIVERY_ZONE,
              {
                address: result.address,
              }
            );
          })
          .catch((error: any) => {
            console.error("Pelias reverse geocoding error:", error);
          });
      } else {
        getGeocode({
          location: { lat: center.lat, lng: center.lng },
        }).then((res) => {
          addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.OUT_OF_DELIVERY_ZONE, {
            address: res[0].address_components,
          });
        });
      }
    }
  };

  // Fill address details from geocode results
  const fillAddressDetailsFromGeocode = () => {
    handleLoadingState(true);

    if (enableInternalMaps) {
      peliasReverseGeocode({
        point: {
          lat: center.lat,
          lon: center.lng,
        },
      })
        .then((result: PeliasResult) => {
          handleLoadingState(false);

          const newDetails = {
            area: "",
            buildingType: "",
            streetName: "",
            buildingNumber: "",
            floorNumber: "",
            apartmentNumber: "",
            addressTitle: "",
            additionalDetails: "",
            lat: center.lat,
            lng: center.lng,
          };

          const address = result.address;
          if (address) {
            newDetails.area = address.city || address.state || "";
            newDetails.streetName =
              address.road ||
              address.suburb ||
              address.neighbourhood ||
              address.village ||
              result.display_name ||
              "";
            newDetails.buildingNumber = address.house_number || "";
          }

          if (!newDetails.streetName) {
            setDisableStreetNameInput(false);
          } else {
            setDisableStreetNameInput(
              (restaurantConfig?.addressFormAutoFill ?? false) && true
            );
          }

          dispatch(setSelectedAddress(newDetails));
        })
        .catch((error: any) => {
          handleLoadingState(false);
          console.log(error.message);
        });
    } else {
      getGeocode({
        location: { lat: center.lat, lng: center.lng },
      })
        .then((res) => {
          handleLoadingState(false);

          const newDetails = {
            area: "",
            buildingType: "",
            streetName: "",
            buildingNumber: "",
            floorNumber: "",
            apartmentNumber: "",
            addressTitle: "",
            additionalDetails: "",
            lat: center.lat,
            lng: center.lng,
          };

          const addressComponents = res[0].address_components;
          for (let i = 0; i < addressComponents.length; i++) {
            switch (addressComponents[i].types[0]) {
              case "street_number":
                newDetails.buildingNumber = addressComponents[i].short_name;
                break;
              case "administrative_area_level_2":
                newDetails.area = addressComponents[i].short_name;
                break;
              case "route":
              case "premise":
              case "sublocality":
              case "sublocality_level_1":
              case "locality":
              case "establishment":
                newDetails.streetName = addressComponents[i].short_name;
                break;
              default:
                break;
            }
          }

          if (!newDetails.streetName) {
            setDisableStreetNameInput(false);
          } else {
            setDisableStreetNameInput(
              (restaurantConfig?.addressFormAutoFill ?? false) && true
            );
          }

          dispatch(setSelectedAddress(newDetails));
        })
        .catch((err) => {
          handleLoadingState(false);
          console.log(err.message);
        });
    }
  };

  // Validate delivery zone and handle outside zone scenarios
  const validateDeliveryZone = (): boolean => {
    if (!currentDeliveryConfig) {
      return true;
    }

    const location = delivery.selectedAddress
      ? {
          lat: delivery.selectedAddress.lat,
          lng: delivery.selectedAddress.lng,
        }
      : center;

    // `currentDeliveryConfig` is the stored row un-re-pointed, and it can
    // belong to another branch than selectedFranchise (see
    // selectStoredDeliveryConfig). Testing ONLY that row's zones made the
    // selected branch's own coverage invisible: a hub guest holding arkan's
    // row for the same delivery type was tested against arkan's polygons, and
    // handleOutsideDeliveryZone deliberately skips the selected branch when it
    // looks for another one -- so an address squarely inside hub was reported
    // as outside delivery. The selected branch's own row for this delivery
    // type is checked first; autoSelectDeliveryFranchise then commits it.
    const selectedFranchiseConfig = (
      selectedFranchise?.franchiseConfig?.deliveryV2 ?? []
    ).find(
      (config) =>
        config.deliveryType.id === currentDeliveryConfig.deliveryType.id
    );

    const inSelectedFranchiseZone =
      !!selectedFranchiseConfig &&
      !!checkInDeliveryZone(
        location,
        selectedFranchiseConfig.deliveryConfigZones
      );

    const inStoredConfigZone =
      inSelectedFranchiseZone ||
      !!checkInDeliveryZone(
        location,
        currentDeliveryConfig.deliveryConfigZones
      );

    if (!inStoredConfigZone) {
      handleOutsideDeliveryZone(delivery.selectedAddress ?? undefined);

      return false;
    }

    return true;
  };

  // Process address completion and fill missing details if needed
  const processAddressCompletion = (): boolean => {
    const allDeliveryInfoFilled = checkAddressKeys(delivery?.selectedAddress);
    if (!allDeliveryInfoFilled) {
      fillAddressDetailsFromGeocode();
      setSelectedAddressState(null);
    }

    return allDeliveryInfoFilled;
  };

  // Handle navigation or show details section based on address completion.
  // newlySelectedFranchise is set only when V3 auto-selects a franchise during confirm;
  // in that case we navigate home with franchiseSlug + orderType params.
  const handleNavigationOrDetails = (
    isAddressComplete: boolean,
    newlySelectedFranchise?: Franchise | null
  ) => {
    if (isAddressComplete) {
      if (newlySelectedFranchise) {
        const link = getPageLink(router, PagesEnum.Home, {
          franchiseSlug: newlySelectedFranchise.slug,
          orderType: OrderTypesEnum.DELIVERYV2,
        });
        router.push(link.href, link.as);
      } else {
        router.push(
          `${
            getPageLink(
              router,
              (router.query[URL_PARAMS.NEXT_PAGE] as PagesEnum) ||
                PagesEnum.Home
            ).as
          }`
        );
      }
    } else {
      setError(null);
      setDetailsSection(true);
      setZoom(17);
    }
  };

  // Prefer the active tab's config (V3, passed as a prop) over redux's
  // currentDeliveryConfig — a dispatch earlier in the same handler doesn't
  // update the redux-selector closure until the next render, so relying on
  // currentDeliveryConfig alone here would scope zone-matching to a stale
  // (or still-null) delivery type instead of the one actually being viewed.
  const getEffectiveDeliveryConfig = () =>
    activeDeliveryConfig !== undefined
      ? activeDeliveryConfig
      : currentDeliveryConfig;

  // Read-only existence check: does this location fall inside ANY franchise's
  // zone for the given delivery type? Used before an address is actually
  // selected/saved, so we don't commit a franchise prematurely.
  const isLocationInAnyFranchiseZone = (
    location: { lat: number; lng: number },
    deliveryTypeId?: number
  ): boolean =>
    franchises.some((f) =>
      (f.franchiseConfig?.deliveryV2 ?? []).some((config) => {
        if (
          deliveryTypeId !== undefined &&
          config.deliveryType.id !== deliveryTypeId
        ) {
          return false;
        }

        return Boolean(
          checkInDeliveryZone(location, config.deliveryConfigZones)
        );
      })
    );

  // Find which franchise's delivery zone contains the confirmed address, then set both
  // selectedFranchise and currentDeliveryConfig to that franchise's matched config.
  // Returns the matched franchise (or the already-selected one in V2 flow).
  const autoSelectDeliveryFranchise = (): Franchise | null => {
    const effectiveDeliveryTypeId =
      getEffectiveDeliveryConfig()?.deliveryType?.id;

    const addressToCheck = delivery.selectedAddress
      ? { lat: delivery.selectedAddress.lat, lng: delivery.selectedAddress.lng }
      : center;

    // Only trust the already-selected franchise as-is when it was already
    // confirmed for this exact delivery type — same condition as
    // handleConfirm's hadFranchiseBefore. Otherwise (e.g. switching from
    // pickup to table-delivery) it may not even offer this type, or the
    // address may fall outside its zone for it, so a full re-search below
    // is required instead of trusting the stale selection.
    if (
      selectedFranchise &&
      currentDeliveryConfig?.deliveryType.id === effectiveDeliveryTypeId
    ) {
      // ...and only when the address being confirmed is genuinely inside THAT
      // franchise's zone for this type. currentDeliveryConfig can belong to a
      // different franchise than selectedFranchise (the V3 tab commits the
      // first matching config it finds), so a matching delivery-type id alone
      // proves nothing about the branch. Returning the stale franchise here
      // kept the customer on their old branch after they picked an address
      // that belongs to another one — prepits/project#3913 UX case 1.
      const selectedFranchiseConfig = (
        selectedFranchise.franchiseConfig?.deliveryV2 ?? []
      ).find((config) => config.deliveryType.id === effectiveDeliveryTypeId);

      // No delivery type in play at all (legacy restaurants with no deliveryV2)
      // — there is no zone to check against, so keep the original behaviour.
      if (effectiveDeliveryTypeId === undefined) {
        return selectedFranchise;
      }

      const inZone =
        selectedFranchiseConfig &&
        checkInDeliveryZone(
          addressToCheck,
          selectedFranchiseConfig.deliveryConfigZones
        );

      if (inZone) {
        dispatch(setCurrentDeliveryConfig(selectedFranchiseConfig!));

        return selectedFranchise;
      }
      // Outside it — fall through to the full search so the branch that DOES
      // cover this address wins.
    }

    for (const f of franchises) {
      const deliveryConfigs = f.franchiseConfig?.deliveryV2 ?? [];
      for (const config of deliveryConfigs) {
        // Only match configs for the delivery type actually being viewed
        if (
          effectiveDeliveryTypeId !== undefined &&
          config.deliveryType.id !== effectiveDeliveryTypeId
        ) {
          continue;
        }
        const inZone = checkInDeliveryZone(
          addressToCheck,
          config.deliveryConfigZones
        );
        if (inZone) {
          dispatch(setSelectedFranchise(f));
          dispatch(setCurrentDeliveryConfig(config));

          // Prevents useFranchiseData from force-reopening the welcome
          // drawer on a later Home mount — it treats a missing cookie as
          // "no franchise ever confirmed".
          const chooseDate = new Date();
          chooseDate.setHours(chooseDate.getHours() + 2);
          // slug can be null on the slug-less main franchise; fall back to id.
          Cookies.set(
            COOKIE_ENUM.LAST_SELECTED_FRANCHISE,
            String(f.slug ?? f.id),
            {
              expires: chooseDate,
            }
          );

          return f;
        }
      }
    }

    return null;
  };

  // Confirm address handler
  const handleConfirm = () => {
    dispatch(setAddressSelectionDrawerState(false));

    const effectiveDeliveryConfig = getEffectiveDeliveryConfig();

    // A franchise only counts as "already confirmed" for THIS flow if it was
    // confirmed for this exact delivery type. Switching tabs (e.g. pickup ->
    // table-delivery) still has the old franchise in selectedFranchise, but
    // for an unrelated type — that must go through full resolution below,
    // not be trusted as-is.
    const hadFranchiseBefore =
      !!selectedFranchise &&
      currentDeliveryConfig?.deliveryType.id ===
        effectiveDeliveryConfig?.deliveryType.id;

    // V3 (activeDeliveryConfig passed by MapScreenV2's tab): a fresh pin/search
    // result with no committed address yet is a "new address" flow that should
    // show the address form below, not immediately escalate to a branch-switch
    // prompt based on the raw pin location — even if a franchise was already
    // established for a *different* previously-saved address.
    // Legacy (V2, activeDeliveryConfig undefined): no such deferred flow —
    // franchise is already known, so validate against its zones immediately,
    // same as before this ever had a V3 concept.
    const isV3Flow = activeDeliveryConfig !== undefined;

    if (
      hadFranchiseBefore &&
      (!isV3Flow || delivery.selectedAddress) &&
      !validateDeliveryZone()
    ) {
      return;
    }

    // V3 flow only (isV3Flow — the V3 map screen is the only caller that passes
    // activeDeliveryConfig): if the user has saved addresses but hasn't selected
    // one yet, prompt them instead of opening the empty address form. Legacy
    // keeps its original behavior of falling through to open the
    // add-new-address form, so this gate avoids trapping legacy users with a
    // red error and no path to enter a new address.
    // If finalValue is set, or the user moved the pin, they are actively adding
    // a new address → proceed to form (the zone check below validates the pin).
    const relevantSavedAddresses = delivery.savedAddresses.filter(
      (a) =>
        a.deliveryTypeId === effectiveDeliveryConfig?.deliveryType?.id ||
        !effectiveDeliveryConfig
    );

    if (
      isV3Flow &&
      relevantSavedAddresses.length > 0 &&
      !delivery.selectedAddress &&
      !finalValue.trim() &&
      isSamePoint(center, initialCenterRef.current)
    ) {
      setError({ text: translations.pleaseSelectSavedAddress });

      return;
    }

    addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.CONFIRM_ADDRESS, {
      pinLocation: {
        lat: center.lat,
        lng: center.lng,
      },
      searchQuery: finalValue,
    });

    // Brand-new pin/search result — no address chosen or saved yet. In V3,
    // this applies whether or not a franchise was already established for a
    // *different* previously-saved address (deferred flow, see above). In
    // legacy (V2), only applies on a genuinely first-ever pin — once a
    // franchise is already known there, validateDeliveryZone above already
    // handled it (returning before reaching here).
    // Only checks whether the pin sits in ANY franchise's zone for the active
    // delivery type; doesn't commit a franchise yet. That happens once a real
    // address exists — either after Save Address, or by re-confirming here
    // with a saved address already selected (the branch below).
    if ((!hadFranchiseBefore || isV3Flow) && !delivery.selectedAddress) {
      const pinInZone = isLocationInAnyFranchiseZone(
        center,
        effectiveDeliveryConfig?.deliveryType?.id
      );

      if (!pinInZone) {
        setError({ text: translations.outsideDelivery });

        return;
      }

      // Commit the active tab's delivery config so the address form/save-address
      // step knows which delivery type this new address belongs to.
      if (activeDeliveryConfig !== undefined) {
        dispatch(setCurrentDeliveryConfig(activeDeliveryConfig));
      }

      // Pre-fill the form from reverse-geocoding the pin — same as
      // processAddressCompletion did for an incomplete address previously,
      // before this branch existed. Without it the form opens fully empty,
      // including fields (e.g. area) the caller may not fill in explicitly.
      fillAddressDetailsFromGeocode();
      setSelectedAddressState(null);

      setError(null);
      setDetailsSection(true);
      setZoom(17);

      return;
    }

    // An address now exists (freshly saved, or picked from the saved list) —
    // resolve and commit the matching franchise for real.
    // In V2, returns selectedFranchise immediately. In V3, iterates all franchises.
    // Deliberately no blanket setCurrentDeliveryConfig(activeDeliveryConfig)
    // ahead of this: committing the tab's config before knowing the address
    // resolves left it committed even when the confirm failed below, which
    // made the NEXT attempt read as "franchise already confirmed for this
    // type" and skip resolution entirely (prepits/project#3913 UX case 1).
    // Every path that succeeds commits the matched franchise's own config.
    const matchedFranchise = autoSelectDeliveryFranchise();

    if (
      !matchedFranchise &&
      effectiveDeliveryConfig &&
      (isV3Flow || !hadFranchiseBefore)
    ) {
      // Address is outside every franchise's delivery zone for this type.
      setError({ text: translations.outsideDelivery });

      return;
    }

    const isAddressComplete = processAddressCompletion();
    // V3 hands the resolved franchise to navigation even when one was already
    // selected: the address just confirmed decides both the branch and the
    // order type. Passing null dropped franchiseSlug/orderType from the link,
    // so DELIVERYV2 was never committed and the customer's previous pick
    // (e.g. "dine in at <branch>") silently stuck. Legacy V2 keeps its
    // original next_page navigation.
    handleNavigationOrDetails(
      isAddressComplete,
      isV3Flow || !hadFranchiseBefore ? matchedFranchise : null
    );
  };

  // Check if all required address fields are filled
  const checkAddressKeys = (address: any) => {
    const keysToCheck = [
      "streetName",
      "buildingNumber",
      "floorNumber",
      "apartmentNumber",
      "addressTitle",
    ];

    if (!address) {
      return false;
    }

    const filteredKeys = keysToCheck.filter((key) => key in address);

    const allKeysFilled = filteredKeys.every((key) => {
      const value = address[key];

      return value !== null && value !== "" && value;
    });

    return filteredKeys.length > 0 && allKeysFilled;
  };

  // Input change handler
  const handleChangeInput = (e: any) => {
    dispatch(
      setSelectedAddress({
        ...delivery.selectedAddress,
        [e.target.name]: e.target.value,
      })
    );
  };

  // Check if column is active
  const checkActiveColumn = (index: string) => {
    return (
      !currentDeliveryConfig ||
      currentDeliveryConfig.deliveryType.activeColumns[
        index as keyof activateAddressColumns
      ]
    );
  };

  // Input validation
  const validateInput = (input: string, required: boolean): string | null => {
    const validationRegex =
      // eslint-disable-next-line no-useless-escape
      /^[\u0621-\u064A\u0660-\u0669a-zA-Z0-9\s\-\.,#/()+\&\u060C'\u2018\u2019]*$/;

    if (!input || input.trim() === "") {
      if (required) {
        return translations.fieldRequired;
      }

      if (input && input.trim() === "") {
        return translations.onlySpaces;
      }
    }

    if (input && !validationRegex.test(input)) {
      return translations.validationMsg;
    }

    return null;
  };

  const handleLoginClick = () => {
    if (restaurantConfig?.hasWelcomeDrawerV3) {
      addCustomerAnalytics(
        CUSTOMER_ANALYTICS_EVENT.CLICKED_LOGIN_FROM_ADDRESSES
      );
    }
    dispatch(setBackPage(PagesEnum.Location));
  };

  // Customer skipped the map/address flow without entering delivery details.
  const reportSkippedAddressForm = () => {
    if (restaurantConfig?.hasWelcomeDrawerV3) {
      addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.SKIPPED_ADDRESS_FORM);
    }
  };

  // Save new address
  const handleSubmitNewAddress = useCallback(
    async (event: any) => {
      event.preventDefault();

      const {
        area,
        streetName,
        buildingNumber,
        floorNumber,
        apartmentNumber,
        addressTitle,
        additionalDetails,
      } = delivery?.selectedAddress as Address;

      // Only validate fields whose column is active for the current delivery
      // type — mirrors both what the form renders and what the API payload
      // sends below. Delivery types with partial activeColumns (e.g.
      // beach-delivery = {}) hide these inputs, so requiring them made the
      // form unsubmittable. Address title is always rendered, so always
      // required.
      if (
        (checkActiveColumn("area") && validateInput(area, true)) ||
        (checkActiveColumn("street_name") && validateInput(streetName, true)) ||
        (checkActiveColumn("building_number") &&
          validateInput(buildingNumber, true)) ||
        (checkActiveColumn("floor_number") &&
          validateInput(floorNumber, true)) ||
        (checkActiveColumn("apartment_number") &&
          validateInput(apartmentNumber, true)) ||
        validateInput(addressTitle, true) ||
        (checkActiveColumn("additional_details") &&
          validateInput(additionalDetails, false))
      ) {
        dispatch(
          setAnimatedGlobalBannerState({
            isOpen: true,
            text: translations.addressError,
            warning: true,
          })
        );

        return;
      }

      setDisableSaveAddressButton(true);
      handleLoadingState(true);

      try {
        const response = await addCustomerAddress(
          checkActiveColumn("area") ? area : null,
          checkActiveColumn("street_name") ? streetName : null,
          checkActiveColumn("building_number") ? buildingNumber : null,
          checkActiveColumn("floor_number") ? floorNumber : null,
          checkActiveColumn("apartment_number") ? apartmentNumber : null,
          addressTitle,
          checkActiveColumn("additional_details") ? additionalDetails : null,
          String(center.lng),
          String(center.lat),
          currentDeliveryConfig
            ? currentDeliveryConfig.deliveryType.id
            : undefined
        );

        dispatch(setSelectedAddress(response.data));
        const newSavedAddresses = [...delivery.savedAddresses, response.data];
        dispatch(setSavedAddresses(newSavedAddresses));

        if (onSaveAddressSuccess) {
          onSaveAddressSuccess();
        } else {
          router.push(
            `${
              getPageLink(
                router,
                (router.query[URL_PARAMS.NEXT_PAGE] as PagesEnum) ||
                  PagesEnum.Home
              ).as
            }`
          );
        }
      } catch (e) {
        console.log(e);
      } finally {
        handleLoadingState(false);
      }
    },
    [delivery?.selectedAddress]
  );

  return {
    // state
    delivery,
    currentDeliveryConfig,
    displayLogin,
    disableSaveAddressButton,
    disableStreetNameInput,
    selectedAddressState,
    // selectors
    orderTypeParam,
    franchiseConfig,
    restaurantConfig,
    // derived
    isEcommerce,
    enableInternalMaps,
    enableNewDeliveryExperience,
    isMainDark,
    zoom,
    detailsSection,
    // autocomplete
    ref,
    finalReady,
    finalValue,
    finalStatus,
    finalData,
    // handlers
    handleInput,
    handleSelect,
    handleSelectSavedLocations,
    handleConfirm,
    handleChangeInput,
    handleSubmitNewAddress,
    handleLoginClick,
    reportSkippedAddressForm,
    handleModal,
    checkActiveColumn,
    validateInput,
    getCurrentPosition,
    // translations
    translations,
  };
};
